import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import {
  VitaBeneficiario,
  VitaBeneficiarioService,
} from '../../services/vita-beneficiario.service';
import {
  CalculatorCountry,
  VitaCalculatorService,
} from '../../services/vita-calculator.service';
import { AddVitaBeneficiarioDialogComponent } from '../vita-calculator-demo/components/add-vita-beneficiario-dialog/add-vita-beneficiario-dialog.component';
import { VitaBeneficiarioDetailDialogComponent } from '../vita-calculator-demo/components/vita-beneficiario-detail-dialog/vita-beneficiario-detail-dialog.component';
import {
  SelectDestinoDialogComponent,
  SelectDestinoResult,
} from './components/select-destino-dialog/select-destino-dialog.component';
import {
  displayBanco,
  displayCuentaMask,
  displayNombre,
  flagAssetFor,
  textoBusqueda,
  tieneRailDeEnvio,
} from '../../utils/beneficiario-internacional.util';

/**
 * Libreta de beneficiarios internacionales (gestión).
 *
 * Es la misma libreta que usa el envío a otros destinos, pero fuera del
 * wizard: acá el cliente da de alta, edita y borra sin tener que arrancar una
 * transferencia. Dos diferencias deliberadas con el paso del wizard:
 *
 *  - El listado NO filtra por país ni por método: muestra todo lo que el
 *    cliente cargó alguna vez, incluidos destinos a los que hoy no se puede
 *    enviar. Ocultarle datos propios sería peor que mostrarle un destino
 *    inactivo.
 *  - El alta SÍ se limita a los países con rail (`PAISES_CON_RAIL`): registrar
 *    a alguien al que no se le puede transferir no le sirve a nadie.
 */
@Component({
  selector: 'app-recipient-internacional',
  templateUrl: './recipient-internacional.component.html',
  styleUrls: ['./recipient-internacional.component.sass'],
})
export class RecipientInternacionalComponent implements OnInit, OnDestroy {
  beneficiarios: VitaBeneficiario[] = [];
  /** Lo que se pinta: filtrado por el buscador y ordenado por país + nombre. */
  filtrados: VitaBeneficiario[] = [];
  loading = true;
  error: string | null = null;

  searchTxt = '';

  readonly displayedColumns = [
    'pais',
    'beneficiario',
    'metodo',
    'cuenta',
    'estado',
    'acciones',
  ];

  /** Destinos habilitados para dar de alta (países activos con rail). */
  paisesDisponibles: CalculatorCountry[] = [];
  paisesLoading = true;

  /** ISO → nombre del catálogo, para la columna País. */
  private nombrePorPais: Record<string, string> = {};

  /** id del beneficiario con una acción en vuelo (ficha / edición / borrado). */
  accionEnCurso: string | null = null;

  /** Card mobile desplegada (una a la vez), igual que las otras libretas. */
  expandedCardId: string | null = null;

  private sub = new Subscription();

  constructor(
    private svc: VitaBeneficiarioService,
    private calc: VitaCalculatorService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.cargarPaises();
    this.cargarBeneficiarios();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // ==================== Carga ====================

  private cargarPaises(): void {
    this.paisesLoading = true;
    this.sub.add(
      this.calc.listCountries().subscribe({
        next: (list) => {
          const countries = list || [];
          this.nombrePorPais = {};
          for (const c of countries) {
            this.nombrePorPais[(c.code || '').toUpperCase()] = c.name;
          }
          this.paisesDisponibles = countries.filter((c) =>
            tieneRailDeEnvio(c.code),
          );
          this.paisesLoading = false;
          this.recomputarFiltrados();
        },
        error: () => {
          // Sin catálogo no se puede dar de alta, pero el listado igual sirve:
          // la columna País cae al código ISO.
          this.paisesDisponibles = [];
          this.paisesLoading = false;
        },
      }),
    );
  }

  cargarBeneficiarios(): void {
    this.loading = true;
    this.error = null;
    // Sin filtro de país: el wizard filtra porque está enviando a un destino
    // concreto; acá se listan todos.
    this.sub.add(
      this.svc.list().subscribe({
        next: (list) => {
          this.beneficiarios = list || [];
          this.loading = false;
          this.recomputarFiltrados();
        },
        error: (err) => {
          this.loading = false;
          this.error =
            typeof err === 'string'
              ? err
              : 'No pudimos cargar tus beneficiarios. Intentá de nuevo.';
        },
      }),
    );
  }

  // ==================== Listado ====================

  onSearchChange(): void {
    this.recomputarFiltrados();
  }

  limpiarBusqueda(): void {
    this.searchTxt = '';
    this.recomputarFiltrados();
  }

  /**
   * Recalcula la lista en un método y no en un getter: un getter devuelve un
   * array nuevo en cada ciclo de detección de cambios, y con `mat-table` eso
   * significa re-renderizar la tabla entera sin parar (mismo problema que ya
   * se corrigió en el wizard).
   */
  private recomputarFiltrados(): void {
    const q = this.searchTxt.trim().toLowerCase();
    const base = q
      ? this.beneficiarios.filter((b) => textoBusqueda(b).includes(q))
      : [...this.beneficiarios];

    this.filtrados = base.sort((a, b) => {
      const paisA = this.paisNombre(a);
      const paisB = this.paisNombre(b);
      if (paisA !== paisB) return paisA.localeCompare(paisB);
      return displayNombre(a).localeCompare(displayNombre(b));
    });
  }

  get totalBeneficiarios(): number {
    return this.beneficiarios.length;
  }

  get hayResultados(): boolean {
    return this.filtrados.length > 0;
  }

  get puedeAgregar(): boolean {
    return !this.paisesLoading && this.paisesDisponibles.length > 0;
  }

  // Helpers de display para el template.
  nombreDe = displayNombre;
  bancoDe = displayBanco;
  cuentaDe = displayCuentaMask;

  paisNombre(b: VitaBeneficiario): string {
    const iso = (b.paisCodigo || '').toUpperCase();
    return this.nombrePorPais[iso] || iso;
  }

  paisFlag(b: VitaBeneficiario): string | null {
    return flagAssetFor(b.paisCodigo);
  }

  /** Los destinos sin rail siguen en la lista, pero se avisa que no se puede enviar. */
  tieneEnvio(b: VitaBeneficiario): boolean {
    return tieneRailDeEnvio(b.paisCodigo);
  }

  esBaneado(b: VitaBeneficiario): boolean {
    return b.vitaEstado === 'banned';
  }

  toggleCard(id: string): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  trackById(_index: number, b: VitaBeneficiario): string {
    return b.id;
  }

  // ==================== Alta ====================

  /**
   * Alta en dos pasos: primero el destino (país + método), que es lo que
   * determina el formulario, y después el formulario dinámico.
   */
  agregar(): void {
    if (!this.puedeAgregar) return;
    const isMobile = window.innerWidth < 768;
    const picker = this.dialog.open(SelectDestinoDialogComponent, {
      data: { countries: this.paisesDisponibles },
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '520px',
      minWidth: isMobile ? '90vw' : '460px',
      maxHeight: '90vh',
    });

    this.sub.add(
      picker.afterClosed().subscribe((destino: SelectDestinoResult | null) => {
        if (!destino) return;
        this.abrirFormulario({
          countryCode: destino.countryCode,
          variant: destino.variant,
          variantLabel: destino.variantLabel,
          schema: destino.schema,
        });
      }),
    );
  }

  /** Abre el formulario dinámico, en alta o en edición según reciba `editId`. */
  private abrirFormulario(data: {
    countryCode: string;
    variant: string;
    variantLabel: string;
    schema: any;
    editId?: string;
    initialValues?: Record<string, any>;
  }): void {
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(AddVitaBeneficiarioDialogComponent, {
      data,
      disableClose: true,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '500px',
      maxHeight: '90vh',
    });

    this.sub.add(
      ref.afterClosed().subscribe((result: VitaBeneficiario | null) => {
        if (!result) return;
        const existe = this.beneficiarios.some((b) => b.id === result.id);
        this.beneficiarios = existe
          ? this.beneficiarios.map((b) => (b.id === result.id ? result : b))
          : [result, ...this.beneficiarios];
        this.recomputarFiltrados();
      }),
    );
  }

  // ==================== Edición ====================

  /**
   * Editar exige el schema de la variante del beneficiario (no el del destino
   * elegido en un alta), así que se pide antes de abrir el formulario.
   */
  editar(b: VitaBeneficiario, event?: Event): void {
    event?.stopPropagation();
    if (this.esBaneado(b)) return;
    this.accionEnCurso = b.id;
    this.sub.add(
      this.calc.getRules(b.variante).subscribe({
        next: (schema) => {
          this.accionEnCurso = null;
          this.abrirFormulario({
            countryCode: b.paisCodigo,
            variant: b.variante,
            variantLabel: b.varianteLabel || b.variante,
            schema,
            editId: b.id,
            initialValues: this.valoresIniciales(b),
          });
        },
        error: () => {
          this.accionEnCurso = null;
          Swal.fire({
            icon: 'error',
            title: 'No se puede editar ahora',
            text: 'No pudimos cargar el formulario de este beneficiario. Intentá más tarde.',
            confirmButtonColor: '#1C593D',
          });
        },
      }),
    );
  }

  /**
   * Reconstruye los valores del formulario: las columnas fijas del beneficiario
   * más el JSON de datos bancarios, con las mismas keys que publica el schema.
   */
  private valoresIniciales(b: VitaBeneficiario): Record<string, any> {
    const values: Record<string, any> = {
      beneficiary_first_name: b.nombre ?? '',
      beneficiary_last_name: b.apellidos ?? '',
      beneficiary_email: b.email ?? '',
      beneficiary_document_type: b.tipoId ?? '',
      beneficiary_document_number: b.numeroId ?? '',
      phone: b.telefono ?? '',
    };
    for (const k of Object.keys(b.datosBancarios || {})) {
      values[k] = b.datosBancarios[k];
    }
    return values;
  }

  // ==================== Ficha ====================

  readonly MSG_BANEADO =
    'Este beneficiario no está habilitado para recibir transferencias. Comunicate con un operador de Aguacate.';
  readonly MSG_INCOMPLETO =
    'Faltan datos de este beneficiario para poder enviarle. Comunicate con un operador de Aguacate para completarlos.';

  /**
   * Abre la ficha en solo lectura. Antes chequea que el beneficiario cumpla las
   * reglas vigentes de su método: si le faltan datos, la ficha lo avisa ahí
   * mismo en vez de que el cliente se entere recién al intentar enviar.
   */
  verFicha(b: VitaBeneficiario, event?: Event): void {
    event?.stopPropagation();
    if (this.esBaneado(b)) {
      this.abrirFicha(b, this.MSG_BANEADO);
      return;
    }
    this.accionEnCurso = b.id;
    this.sub.add(
      this.svc.completitud(b.id).subscribe({
        next: (res) => {
          this.accionEnCurso = null;
          const incompleto = !res.valid && (res.faltantes?.length || 0) > 0;
          this.abrirFicha(b, incompleto ? this.MSG_INCOMPLETO : null);
        },
        error: () => {
          // Best-effort: si el chequeo falla, la ficha se abre igual.
          this.accionEnCurso = null;
          this.abrirFicha(b, null);
        },
      }),
    );
  }

  private abrirFicha(b: VitaBeneficiario, aviso: string | null): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(VitaBeneficiarioDetailDialogComponent, {
      data: { beneficiario: b, hideUse: true, aviso },
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '480px',
      maxHeight: '90vh',
    });
  }

  // ==================== Baja ====================

  async eliminar(b: VitaBeneficiario, event?: Event): Promise<void> {
    event?.stopPropagation();
    const result = await Swal.fire({
      title: '¿Eliminar beneficiario?',
      text: `${displayNombre(b)} dejará de aparecer en tu libreta. Los envíos que ya le hiciste no se modifican.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    this.accionEnCurso = b.id;
    this.sub.add(
      this.svc.archive(b.id).subscribe({
        next: () => {
          this.accionEnCurso = null;
          this.beneficiarios = this.beneficiarios.filter((x) => x.id !== b.id);
          this.recomputarFiltrados();
        },
        error: (err) => {
          this.accionEnCurso = null;
          Swal.fire({
            icon: 'error',
            title: 'No se pudo eliminar',
            text:
              typeof err === 'string'
                ? err
                : 'Intentá de nuevo en unos minutos.',
            confirmButtonColor: '#1C593D',
          });
        },
      }),
    );
  }
}
